import { Formik } from 'formik';
import React, { useMemo } from 'react';
import { Button, Form } from 'react-bootstrap';

import { useEntitiesCreate, useProfileUpdate } from '../../../hooks';
import { autocompleteEntities } from '../../../utils/backend-api/entities';
import { Divider } from '../../BaseUI';
import { AutocompleteField, TextAreaField, TextField } from '../../FormFields';
import ProfileSchema from './ProfileSchema';

const Profile = ({
  userId,
  oneLineBio,
  username,
  company,
  location,
  title,
  bio,
  shortBio,
  isStaff,
  isDisabled,
}) => {
  const initialState = useMemo(
    () => ({
      username,
      oneLineBio: oneLineBio || '',
      company: company || '',
      location: location || '',
      title: title || '',
      bio: bio || '',
      shortBio: shortBio || '',
    }),
    [username, company, location, title, oneLineBio, shortBio, bio]
  );
  const { mutateAsync: createEntityAsync } = useEntitiesCreate();
  const { saveProfile, isSavingProfile, showSave } = useProfileUpdate(userId);

  const handleSubmit = (values) => {
    const sanitizedParams = {
      username: values.username || '',
      company_name: values.company.label || '',
      title_at_company: values.title.label || '',
      location: values.location.value || '',
      one_line_bio: values.oneLineBio || '',
      short_bio: values.shortBio || '',
      bio: values.bio || '',
    };

    saveProfile(sanitizedParams);
  };

  return (
    <Formik
      enableReinitialize
      initialValues={initialState}
      onSubmit={handleSubmit}
      validationSchema={ProfileSchema}
    >
      {(formik) => (
        <Form noValidate onSubmit={formik.handleSubmit}>
          <div className="section-container" id="Profile">
            <div className="section-header mt-2">Profile</div>
            <div className="section-detail">
              <div className="section-detail-container">
                <div className="section-description">
                  <div>Tell us about you</div>
                </div>
              </div>
              <div className="section-form-container">
                <TextField
                  disabled={isDisabled}
                  name="username"
                  label="Profile URL"
                  pretype="https://theinformation.com/u/"
                  placeholder="username"
                />
                <AutocompleteField
                  isDisabled={isDisabled}
                  label="Location"
                  name="location"
                  fetchMethod={(value) =>
                    autocompleteEntities({
                      fragment: value,
                      entity_type: 'location',
                      word_type: 'proper',
                    })
                  }
                  createMethod={(value) =>
                    createEntityAsync({
                      name: value,
                      entity_type: 'location',
                      word_type: 'proper',
                    })
                  }
                />
                <AutocompleteField
                  isDisabled={isDisabled}
                  label="Title"
                  name="title"
                  fetchMethod={(value) =>
                    autocompleteEntities({
                      fragment: value,
                      entity_type: 'job_title',
                    })
                  }
                  createMethod={(value) =>
                    createEntityAsync({
                      name: value,
                      entity_type: 'other',
                      word_type: 'common',
                    })
                  }
                />
                <AutocompleteField
                  isDisabled={isDisabled}
                  label="Company"
                  name="company"
                  fetchMethod={(value) =>
                    autocompleteEntities({
                      fragment: value,
                      entity_type: 'organization',
                      word_type: 'proper',
                    })
                  }
                  createMethod={(value) =>
                    createEntityAsync({
                      name: value,
                      entity_type: 'organization',
                      word_type: 'proper',
                    })
                  }
                />
                <TextField
                  name="oneLineBio"
                  label="One-line bio / pronouns"
                  disabled={isDisabled}
                  maxCharCount={40}
                />
                {isStaff && (
                  <TextAreaField
                    disabled={isDisabled}
                    name="shortBio"
                    label="Short bio"
                    placeholder="Please enter a sentence or two description of yourself."
                    rows={4}
                  />
                )}
                <TextAreaField
                  disabled={isDisabled}
                  name="bio"
                  label="Bio"
                  placeholder="Short description about me"
                />
              </div>
            </div>
            <div className="save-action-container" id="profile-save">
              <Button
                className="profile-button --red --small-width"
                type="submit"
                disabled={!(formik.isValid && formik.dirty) || isDisabled || isSavingProfile}
              >
                {isSavingProfile ? 'Saving' : showSave ? 'Saved' : 'Save'}
              </Button>
            </div>
            <Divider />
          </div>
        </Form>
      )}
    </Formik>
  );
};

export default Profile;
